/**
 * WP4 M4 — время «переключил дату/режим → тайлы отрисованы» (карта).
 *
 * Founder-жалоба дословно (аудит 21.08, `docs/superpowers/specs/
 * 2026-08-21-map-mv-audit.md` §8): «выбрал беломечётское, открыл май и
 * последовательно каждую дату включаю, ищу чистые снимки в режиме gnr и
 * ndvi контрастный — каждое переключение ппц как долго рендерится на
 * фронте». `useRasterTilesLoading` уже знает МОМЕНТЫ старта/конца загрузки
 * по каждому `loadToken` (дата+режим) — этот модуль просто меряет
 * длительность между ними и публикует её (Sentry breadcrumb сейчас;
 * `getLastRasterSwitch()` — синхронный геттер для дальнейшей интеграции
 * с дашбордом/E2E-ассертом).
 *
 * Почему ключ — (sourceId, token), а не только token (head-backend review
 * PR #909 N1)
 * ------------------------------------------------------------------------
 * `useRasterTilesLoading` используется ДВАЖДY одновременно на разных
 * поверхностях: `/map` (`pages/map/v2.tsx`, source `ndvi-active-raster`) и
 * мини-карта карточки поля (`FieldOverviewTab.tsx`, source `field-ndvi-raster`
 * через `NDVI_SOURCE_ID`) — обе МОГУТ быть смонтированы одновременно (панель
 * карточки поля открывается поверх /map). Модульный синглтон без ключа по
 * `sourceId` означал бы: старт замера на источнике B перетирает pending-запись
 * источника A, и когда A завершится, `finishRasterSwitch` либо вернёт `null`
 * (token не совпал — молча теряем замер A), либо, что хуже, СОВПАДЁТ по
 * случайности формата токена и припишет A длительность, вычисленную от
 * времени старта B. Ключуем `Map` по `sourceId`, чтобы два хука никогда не
 * делили одну pending-запись.
 *
 * Почему отдельный модуль, а не логика внутри хука
 * ----------------------------------------------------
 * Чистая timing-функция без побочных эффектов React (no useState/useEffect)
 * тестируется без рендера компонента и без мока MapLibre — конструктору
 * теста достаточно вызвать `startRasterSwitch`/`finishRasterSwitch` с
 * ЗАРАНЕЕ известными `nowMs` и сверить точную длительность (см.
 * `rasterSwitchMetrics.test.ts`). Реальный wall-clock (`performance.now()`)
 * передаётся ВЫЗЫВАЮЩИМ кодом (хуком) — сам модуль часов не читает, поэтому
 * не может быть флейки по таймингу теста.
 *
 * Канал (head-backend review PR #909 B2 — тот же класс дефекта, что чинит
 * этот PR на бэкенде: измеритель без работающего экспорта)
 * ------------------------------------------------------------------------
 * `Sentry.addBreadcrumb` физически не может попасть в Sentry без `VITE_SENTRY_DSN`
 * в бандле — а Vite инлайнит `VITE_*` на build-time, из ``ARG`` в
 * `frontend-vite/Dockerfile`. До 2026-08-24 этого ARG не было НИГДЕ (ни в
 * Dockerfile, ни в `docker-compose.yml build.args`, ни в одном воркфлое) —
 * `initSentry()` был гарантированным no-op в ЛЮБОМ задеплоенном билде, и
 * breadcrumb уходил в никуда молча. Проведена та же правка, что и для
 * бэкенда (`instrument_app`/`MeterProvider`): ARG/ENV в Dockerfile +
 * build-arg в docker-compose.yml. Значение DSN — из серверного `.env`/
 * `.env.staging` (тот же путь, что у `VITE_API_URL`/`VITE_APP_NAME` —
 * деплой-воркфлоу их НЕ инжектит), пусто по умолчанию → тот же
 * no-op-if-unset, что у бэкендового `SENTRY_DSN` (`sentry_init.py`).
 * Если DSN на сервере всё ещё не задан — breadcrumb остаётся no-op
 * ОСОЗНАННО (не молча), проверить: `getLastRasterSwitch()` в консоли браузера.
 *
 * Что НЕ измеряет (head-backend review PR #909 N2) — читать перед цитированием числа
 * ------------------------------------------------------------------------------------
 * Часы останавливаются в `useRasterTilesLoading` и на `sourcedata`/`idle`
 * (тайлы реально дорисовались), И на `onError` (ошибка рендера/сети), И на
 * MapLibre `idle` как «глобальный backstop». Оборванная (`error`) или
 * отменённая (юзер тут же щёлкнул следующую дату) загрузка тоже
 * останавливает часы — и попадает в статистику как быстрое «тёплое»
 * переключение, хотя тайлы физически не отрисовались. Серия мерит
 * «загрузка прекратилась», а не строго «тайлы отрисованы» — при
 * агрегации (напр. p75/p95 по `cold`) высокая доля быстрых ошибок/отмен
 * будет тянуть число ВНИЗ, маскируя реальную медленность. Если процент
 * `onError`-остановок велик — число нужно перепроверять, не брать на веру.
 */
import * as Sentry from '@sentry/react';

/** Выше этого порога переключение считается «холодным» (не тёплый повтор из кэша). */
export const COLD_THRESHOLD_MS = 1500;

interface PendingSwitch {
  token: string;
  startedAtMs: number;
}

export interface RasterSwitchResult {
  sourceId: string;
  token: string;
  durationMs: number;
  cold: boolean;
  /** `nowMs` переданный в `finishRasterSwitch` — момент завершения замера, для сортировки "последний по времени". */
  finishedAtMs: number;
}

const pending = new Map<string, PendingSwitch>();
const lastResults = new Map<string, RasterSwitchResult>();

/** Отметить начало загрузки `token` на источнике `sourceId`. Перетирает незавершённый предыдущий замер ТОГО ЖЕ источника. */
export function startRasterSwitch(sourceId: string, token: string, nowMs: number): void {
  pending.set(sourceId, { token, startedAtMs: nowMs });
}

/**
 * Завершить замер для `(sourceId, token)`. Возвращает `null`, если для этого
 * `sourceId` либо ничего не измерялось, либо измерялся другой токен
 * (устаревший/отменённый переключатель — MapLibre успел переключиться ещё
 * раз, пока грузился первый) — на призрачный токен breadcrumb не шлём, чтобы
 * не путать длительности разных дат.
 */
export function finishRasterSwitch(sourceId: string, token: string, nowMs: number): RasterSwitchResult | null {
  const entry = pending.get(sourceId);
  if (!entry || entry.token !== token) return null;
  const durationMs = Math.max(0, nowMs - entry.startedAtMs);
  pending.delete(sourceId);
  const cold = durationMs > COLD_THRESHOLD_MS;
  const result: RasterSwitchResult = { sourceId, token, durationMs, cold, finishedAtMs: nowMs };
  lastResults.set(sourceId, result);

  Sentry.addBreadcrumb({
    category: 'map.raster-switch',
    message: cold ? 'raster switch (cold)' : 'raster switch (warm)',
    level: 'info',
    data: { sourceId, token, durationMs: Math.round(durationMs), cold },
  });

  return result;
}

/**
 * Последний завершённый замер — для дебага/E2E-ассертов, не для React-рендера.
 * Без `sourceId` — самый недавний по `finishedAtMs` среди всех источников
 * (НЕ порядок вставки `Map` — `Map.set` на существующий ключ не двигает его
 * позицию, поэтому insertion-order было бы неверным способом найти «последний»).
 */
export function getLastRasterSwitch(sourceId?: string): RasterSwitchResult | null {
  if (sourceId !== undefined) return lastResults.get(sourceId) ?? null;
  let latest: RasterSwitchResult | null = null;
  for (const result of lastResults.values()) {
    if (latest === null || result.finishedAtMs > latest.finishedAtMs) latest = result;
  }
  return latest;
}

/** Сброс модульного состояния между тестовыми сценариями. */
export function __resetRasterSwitchMetricsForTests(): void {
  pending.clear();
  lastResults.clear();
}
